import { mediaBuyTitle, PageTitle } from '@/features/shared/page-title';
import {
  ExpandableSidebarSection,
  PageContentSection,
  PageSection,
  TopSection
} from '@/components/layout/layout';
import { MediaPlanBreadcrumbs } from '@/components/layout/breadcrumbs';
import React from 'react';
import { useCampaignContext } from '@/features/campaigns/campaign-context';
import { useLineItemContext } from '@/features/line-items/line-items-context';
import { useParams } from 'react-router-dom';
import { MediaPlan } from 'shared/src/domain/media-buy/media-buy-types';
import { MediaPlanMetrics } from 'shared/src/domain/metrics/metrics-types';
import { MediaPlanHeroMetrics } from '@/features/media-plans/media-plan-hero-metrics';
import { HeroMetricMessage, HeroMetricsContainer } from '@/components/display/hero-metrics';
import { faChartLine, faCode, faList } from '@fortawesome/pro-light-svg-icons';
import { DetailsRail } from '@/components/display/details-rail';
import { PacingSchedule } from '@/features/line-items/pacing-schedule';
import { useExecutionContext } from '@/features/executions/execution-context';
import ReactJsonView from '@microlink/react-json-view';
import { MediaPlanApiResponse } from 'shared/src/api/media-plan-api-types';
import { MediaPlanGraph } from '@/features/executions/line-item-graph';
import { Granularity } from '@/components/charts/chart-type-selector';
import { useCaptureMediaPlanView } from '@/utils/posthog/analytics';

export function MediaPlanPage({
  granularity,
  setGranularity
}: {
  granularity: Granularity;
  setGranularity: (granularity: Granularity) => void;
}) {
  const { campaign } = useCampaignContext();
  const { execution } = useExecutionContext();
  const { lineItem, mediaPlans, mediaPlansMetrics } = useLineItemContext();
  const { mediaPlanId } = useParams();
  const mediaPlan = findMediaPlan(mediaPlans, mediaPlanId);
  const mediaPlanMetrics = findMediaPlanMetrics(mediaPlansMetrics.data, mediaPlanId);
  useCaptureMediaPlanView(mediaPlan);

  if (!mediaPlan) {
    return <div>Media Plan not found</div>;
  }

  return (
    <div className="flex w-full flex-col">
      <PageTitle title={mediaBuyTitle(lineItem.name)} />
      <TopSection>
        <MediaPlanBreadcrumbs campaign={campaign} lineItem={lineItem} mediaPlan={mediaPlan} />
      </TopSection>
      <PageSection>
        <PageContentSection>
          {mediaPlanMetrics ? (
            <MediaPlanHeroMetrics lineItem={lineItem} metrics={mediaPlanMetrics} />
          ) : (
            <HeroMetricsContainer>
              <HeroMetricMessage
                message="No delivery data available. "
                additionalMessage="Metrics will appear once the platform reports delivery data for this media plan."
              />
            </HeroMetricsContainer>
          )}
          <MediaPlanGraph
            granularity={granularity}
            setGranularity={setGranularity}
            mediaPlanId={mediaPlan.id}
          />
        </PageContentSection>
        <ExpandableSidebarSection
          sections={[
            ...(lineItem && mediaPlanMetrics
              ? [
                  {
                    name: 'details',
                    header: 'Media Plan Details',
                    icon: faList,
                    render: (setExpanded: (expanded: string) => void) => (
                      <DetailsRail
                        strategy={execution}
                        lineItem={lineItem}
                        setExpanded={setExpanded}
                      />
                    )
                  }
                ]
              : []),
            ...(lineItem && lineItem.pacing_type === 'custom'
              ? [
                  {
                    name: 'pacing',
                    header: 'Pacing Schedule',
                    icon: faChartLine,
                    render: () => <PacingSchedule strategy={execution} lineItem={lineItem} />
                  }
                ]
              : []),
            {
              name: 'config',
              header: 'Config',
              icon: faCode,
              render: () => <RawConfigSection mediaPlan={mediaPlan} />
            }
          ]}
        />
      </PageSection>
    </div>
  );
}

function findMediaPlan(mediaPlans: MediaPlan[], mediaPlanId: string | undefined) {
  return mediaPlans.find(mediaPlan => mediaPlan.id === mediaPlanId);
}

function findMediaPlanMetrics(
  mediaPlanMetrics: MediaPlanMetrics[],
  mediaPlanId: string | undefined
) {
  return mediaPlanMetrics.find(metrics => metrics.media_plan_id === mediaPlanId);
}

function RawConfigSection({ mediaPlan }: { mediaPlan: MediaPlanApiResponse | undefined }) {
  return (
    <div className="flex h-full flex-col">
      <ReactJsonView src={mediaPlan?.media_buy?.media_platform_entity?.raw_entity || {}} />
    </div>
  );
}
